import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, map, of, switchMap, tap } from 'rxjs';
import { Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { TranslocoService } from '@jsverse/transloco';

import { CollectionItemApiService } from '../services/collection-item-api.service';
import {
  confirmDeleteCollectionItem,
  deleteCollectionItem,
  deleteCollectionItemAborted,
  deleteCollectionItemError,
  deleteCollectionItemSuccess,
  extendWarranty,
  extendWarrantyError,
  extendWarrantySuccess,
  loadCollectionItem,
  loadCollectionItemError,
  loadCollectionItemSuccess,
  userCanceledWarranty,
  userNotAllowedToExtendWarranty,
  verifyWarranty,
  verifyWarrantyFailed,
} from './collection-item.actions';
import { ConfirmPopupComponent } from '@components/confirm-popup/confirm-popup.component';
import { WarrantyInfoDialogComponent } from '../components/dialogs/warranty-info-dialog/warranty-info-dialog.component';
import { loadCollection } from '../../collection/store/collection.actions';
import {
  CommonService,
  DisclosureType,
} from '../../../shared/services/common.service';
import { VerifyResponse } from '../collection-item.model';
import { ErrorDialogComponent } from '../../../shared/components/error-dialog/error-dialog.component';

@Injectable()
export class CollectionItemEffects {
  private readonly actions$ = inject(Actions);
  private readonly collectionItemApiService = inject(CollectionItemApiService);
  private readonly dialog = inject(MatDialog);
  private readonly bottomSheet = inject(MatBottomSheet);
  private readonly router = inject(Router);
  private readonly transloco = inject(TranslocoService);
  private readonly common = inject(CommonService);

  private readonly loadCollectionItem$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(loadCollectionItem),
      switchMap(({ collectionItemId }) =>
        this.collectionItemApiService.getCollectionItem(collectionItemId).pipe(
          map((collectionItem) => loadCollectionItemSuccess({ collectionItem })),
          catchError(() => of(loadCollectionItemError())),
        ),
      ),
    );
  });

  private readonly deleteCollectionItem$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(deleteCollectionItem),
      switchMap(({ collectionItemId }) =>
        this.collectionItemApiService.deleteCollectionItem(collectionItemId).pipe(
          map(() => deleteCollectionItemSuccess()),
          catchError(() => of(deleteCollectionItemError())),
        ),
      ),
    );
  });

  private readonly redirectAfterDelete$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(deleteCollectionItemSuccess, loadCollectionItemError),
      tap(() => {
        void this.router.navigate(['/tabs/collection']);
      }),
      switchMap(() => {
        return of(loadCollection({ loading: true, reset: true }));
      }),
    );
  });

  private readonly confirmDeleteCollectionItem$ = createEffect(() =>
    this.actions$.pipe(
      ofType(confirmDeleteCollectionItem),
      switchMap(({ collectionItemId }) => {
        const dialogRef = this.dialog.open(ConfirmPopupComponent, {
          id: 'confirmDeleteCollectionItem',
          data: { collectionItemId },
          width: '30rem',
          disableClose: true,
        });

        return dialogRef.afterClosed().pipe(
          map((confirmed) => ({ confirmed, collectionItemId })),
        );
      }),
      map(({ confirmed, collectionItemId }) =>
        confirmed
          ? deleteCollectionItem({ collectionItemId })
          : deleteCollectionItemAborted(),
      ),
      catchError(() => of(deleteCollectionItemAborted())),
    ),
  );

  private readonly verifyWarranty$ = createEffect(() =>
    this.actions$.pipe(
      ofType(verifyWarranty),
      switchMap(({ collectionItemId }) =>
        this.collectionItemApiService.verifyWarranty().pipe(
          switchMap((res: VerifyResponse) => {
            if (res.status !== 'success') {
              this.bottomSheet.open(WarrantyInfoDialogComponent, {
                panelClass: 'app-bottom-sheet-panel',
                data: {
                  title: this.transloco.translate(
                    'collection_item.warranty_procedure_title',
                  ),
                  description: this.transloco.translate(
                    'collection_item.warranty_missing_address_description',
                  ),
                  confirmLabel: 'OK',
                },
              });

              return of(userNotAllowedToExtendWarranty());
            }

            return this.common
              .getPolicyList(DisclosureType.WARRANTY, 'my_visionnaire')
              .pipe(
                switchMap((policyList) => {
                  const warrantyPolicy = policyList.result?.data?.[0];
                  const policyId = warrantyPolicy?.id;

                  if (!policyId) {
                    return of(verifyWarrantyFailed());
                  }

                  const acceptanceLinkUrl =
                    warrantyPolicy?.url ??
                    warrantyPolicy?.link ??
                    warrantyPolicy?.documentUrl ??
                    warrantyPolicy?.document?.url ??
                    null;

                  const sheetRef = this.bottomSheet.open(
                    WarrantyInfoDialogComponent,
                    {
                      panelClass: 'app-bottom-sheet-panel',
                      disableClose: false,
                      data: {
                        title: 'Warranty Terms Acceptance',
                        description:
                          'The extension is subject to the correct provision of the required identification and personal data, as outlined in the following conditions.',
                        requiresAcceptance: true,
                        acceptancePrefix:
                          'I declare that I have read and I accept the ',
                        acceptanceLinkLabel: 'Extended Warranty Conditions',
                        acceptanceSuffix: ' provided by IPE srl',
                        acceptanceLinkUrl,
                        confirmLabel: 'Confirm',
                      },
                    },
                  );

                  return sheetRef.afterDismissed().pipe(
                    map((confirmed: boolean | undefined) =>
                      confirmed
                        ? extendWarranty({
                          collectionItemId,
                          policyId,
                        })
                        : userCanceledWarranty(),
                    ),
                  );
                }),
              );
          }),
          catchError(() => of(verifyWarrantyFailed())),
        ),
      ),
    ),
  );

  private readonly extendWarranty$ = createEffect(() =>
    this.actions$.pipe(
      ofType(extendWarranty),
      switchMap(({ collectionItemId, policyId }) =>
        this.collectionItemApiService.extendWarranty(collectionItemId, policyId).pipe(
          map((response) => {
            if (response.result && response.result.error) {
              return extendWarrantyError({ error: response.result.error });
            }

            return extendWarrantySuccess({ collectionItemId });
          }),
          catchError((err) => of(extendWarrantyError({ error: err }))),
        ),
      ),
    ),
  );

  private readonly onExtendSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(extendWarrantySuccess),
      switchMap(({ collectionItemId }) => {
        this.bottomSheet.open(WarrantyInfoDialogComponent, {
          panelClass: 'app-bottom-sheet-panel',
          data: {
            title: this.transloco.translate(
              'collection_item.warranty_procedure_title',
            ),
            description: this.transloco.translate('collection_item.warranty_title'),
            confirmLabel: 'OK',
          },
        });

        return of(loadCollectionItem({ collectionItemId }));
      }),
    ),
  );

  private readonly onExtendFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(extendWarrantyError),
        tap(({ error }) => {
          this.dialog.open(ErrorDialogComponent, {
            width: '30rem',
            data: {
              message: error.message,
            },
          });
        }),
      ),
    { dispatch: false },
  );
}
